import { looksLikeProse } from '@odhaczka/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { aiCall, aiDisabled } from '../../data/ai';

/** Limit tekstu /api/ai/parse. */
const MAX_TEXT = 4000;

interface Props {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Wiadomość pisana zdaniami („weź jeszcze masło i 2 chleby”) → pozycje listy przez AI.
 * Gdy tekst wygląda na wiadomość, proponuje rozpisanie; poza tym zawsze jest akcja „Rozpisz z AI”.
 * Wynik zastępuje tekst w polu (dalej edytowalny), a „Cofnij” przywraca oryginał.
 */
export function ProseAssist({ value, onChange }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; message: string } | null>(null);
  const [undo, setUndo] = useState<{ original: string; result: string } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const request = useRef<{ text: string; ctrl: AbortController } | null>(null);
  const prose = useMemo(() => looksLikeProse(value), [value]);

  // Edycja po rozpisaniu — „Cofnij” nadpisałoby zmiany, więc znika.
  if (undo && value !== undo.result) setUndo(null);

  // Tekst zmieniony w trakcie rozpisywania: wynik byłby nieaktualny.
  useEffect(() => {
    if (request.current && request.current.text !== value) {
      request.current.ctrl.abort();
      request.current = null;
      setBusy(false);
    }
  }, [value]);
  useEffect(() => () => request.current?.ctrl.abort(), []);

  const split = async () => {
    const original = value;
    const ctrl = new AbortController();
    request.current = { text: original, ctrl };
    setBusy(true);
    setError(null);
    const res = await aiCall<{ lines?: unknown }>('/api/ai/parse', { text: original }, ctrl.signal);
    if (ctrl.signal.aborted) return;
    request.current = null;
    setBusy(false);
    const lines = Array.isArray(res?.lines) ? res.lines.filter((l): l is string => typeof l === 'string' && l.trim() !== '') : null;
    if (!lines) return setError({ text: original, message: 'Nie udało się rozpisać (brak połączenia?)' });
    if (lines.length === 0) return setError({ text: original, message: 'Nie znalazłem tu nic do kupienia.' });
    const result = lines.join('\n');
    setUndo({ original, result });
    onChange(result);
  };

  const restore = () => {
    if (!undo) return;
    setUndo(null);
    // Oryginał to wiadomość — nie proponujemy od razu ponownie.
    setDismissed(true);
    onChange(undo.original);
  };

  if (undo)
    return (
      <Bar>
        <span className="min-w-0 flex-1">Rozpisane przez AI — sprawdź pozycje.</span>
        <BarButton onClick={restore}>Cofnij</BarButton>
      </Bar>
    );
  if (busy) return <p className="px-1 text-sm text-stone-500 motion-safe:animate-pulse">Rozpisuję na pozycje…</p>;
  if (error?.text === value) return <p className="px-1 text-sm text-red-600 dark:text-red-400">{error.message}</p>;
  if (!value.trim() || value.length > MAX_TEXT || aiDisabled()) return null;
  if (prose && !dismissed)
    return (
      <Bar>
        <span className="min-w-0 flex-1">To wygląda na wiadomość. Rozpisać na pozycje?</span>
        <BarButton primary onClick={() => void split()}>
          Rozpisz
        </BarButton>
        <button
          type="button"
          aria-label="Nie rozpisuj"
          onClick={() => setDismissed(true)}
          className="flex size-11 shrink-0 items-center justify-center rounded-lg text-xl text-stone-500 active:bg-violet-100 dark:active:bg-violet-900/40"
        >
          ×
        </button>
      </Bar>
    );
  return (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={() => void split()}
        className="min-h-11 rounded-lg px-3 text-sm font-medium text-brand-700 active:bg-brand-50 dark:text-brand-500 dark:active:bg-stone-800"
      >
        Rozpisz z AI
      </button>
    </div>
  );
}

function Bar({ children }: { children: React.ReactNode }) {
  return (
    <div role="status" className="flex items-center gap-2 rounded-xl bg-violet-50 py-1 pr-1 pl-3 text-sm dark:bg-violet-900/20">
      {children}
    </div>
  );
}

function BarButton({ primary = false, onClick, children }: { primary?: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-11 shrink-0 rounded-lg px-3 font-medium ${
        primary ? 'bg-brand-700 text-white active:bg-brand-800' : 'text-brand-700 active:bg-violet-100 dark:text-brand-500 dark:active:bg-violet-900/40'
      }`}
    >
      {children}
    </button>
  );
}
